import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api, {
  createPayFastLiveTestAppOrder,
  createPayFastLiveTestLandingOrder,
  fetchPayFastLiveTestStatus,
  type CommerceBuildRequest,
  type CommerceBuildRequestStatus,
  type PayFastLiveTestStatus,
} from '../services/api';
import { rememberPendingCommerceOrder, submitPayFastHostedPayment } from '../services/payfast';
import SimButton from '../components/ui/SimButton';
import EmployerHeader from '../components/employer/EmployerHeader';
import SimCard from '../sim/ui/SimCard';
import SimShell from '../sim/ui/SimShell';
import { SimBody, SimLabel, SimTitle } from '../sim/ui/SimText';

type AdminTab =
  | 'import'
  | 'simulations'
  | 'variants'
  | 'credits'
  | 'buildRequests'
  | 'payfastTest'
  | 'links'
  | 'attempts'
  | 'reports';

type Wallet = {
  id: string;
  company_id?: string;
  name: string;
  wallet: {
    balance: number;
    reserved_balance: number;
    credits_available: number;
  };
};

type VariantItem = {
  id: string;
  variant_code: string;
  variant_name: string;
  is_gold_demo: boolean;
  simulation: {
    id: string;
    name: string;
    version: string;
  };
  created_at: string;
};

type SimulationAdminItem = {
  simulation_code: string;
  name: string;
  role_family: string;
  is_globally_enabled: boolean;
  visibility: 'PUBLIC' | 'PRIVATE';
  version_count: number;
  active_version: {
    id: string;
    name: string;
    version: string;
    created_at: string;
  } | null;
  latest_version: {
    id: string;
    name: string;
    version: string;
    created_at: string;
    is_active_version: boolean;
  } | null;
};

type SimulationVersionItem = {
  id: string;
  code: string;
  name: string;
  version: string;
  roleFamily: string;
  isActiveVersion: boolean;
  variantCount?: number;
  screenCount?: number;
  createdAt: string;
  updatedAt: string;
};

type SimulationAccessItem = {
  company_id: string;
  company_name: string;
  enabled: boolean;
};

type LinkItem = {
  id: string;
  created_at: string;
  public_url: string;
  company: { id: string; name: string };
  simulation: { name: string; version: string };
  variant_code: string | null;
  variant_name: string | null;
  variant_policy: 'ROTATE' | 'FIXED';
  job_title: string | null;
  status: 'ACTIVE' | 'EXPIRED' | 'DISABLED';
  expires_at: string | null;
  disabled_at: string | null;
};

type AttemptItem = {
  id: string;
  status: string;
  created_at: string;
  completed_at: string | null;
  score_summary: Record<string, unknown> | null;
  integrity_rating: string | null;
  variant: { id: string; code: string; name: string };
  candidate: {
    name: string;
    id_last4: string | null;
    full_name?: string;
    sa_id_last4?: string | null;
  };
  company: { id: string; name: string };
  link: { id: string; job_title: string | null };
};

type ReportItem = {
  id: string;
  attempt_id: string;
  created_at: string;
  status: string;
  company: {
    id: string;
    name: string;
  };
  variant: { id: string; code: string; name: string; job_title: string | null } | null;
  candidate: { full_name: string; sa_id_last4: string } | null;
  performance: {
    percent: number;
    band: string;
  };
  report_url: string | null;
};

type PagedResponse<T> = {
  items: T[];
  hasMore: boolean;
  nextCursor: string | null;
};

type AdminListError = { code: string; message: string };
type ImportLogLine = {
  ts?: string;
  stream?: 'stdout' | 'stderr';
  text: string;
};
type AdminImportResponse = {
  ok: boolean;
  mode?: 'dry-run' | 'commit';
  summary?: {
    simulationName: string;
    simulationVersion: string;
    variantCount: number;
    screenCount: number;
    rowCount: number;
    mode: 'dry-run' | 'commit';
    elapsedMs: number;
  };
  logs?: string | string[] | ImportLogLine[];
  error?: {
    code?: string;
    message: string;
    details?: AdminListError[];
  };
};

type AdminImportPreflightIssue = {
  rule: string;
  message: string;
  sheet: string;
  row: number;
  severity: 'ERROR' | 'WARN';
};

type AdminImportPreflightResponse = {
  ok: boolean;
  errors: AdminImportPreflightIssue[];
  warnings: AdminImportPreflightIssue[];
};

const tabOrder: { key: AdminTab; label: string }[] = [
  { key: 'import', label: 'Import' },
  { key: 'simulations', label: 'Simulations' },
  { key: 'variants', label: 'Variants' },
  { key: 'credits', label: 'Companies + Credits' },
  { key: 'buildRequests', label: 'Build Requests' },
  { key: 'payfastTest', label: 'PayFast Test' },
  { key: 'links', label: 'Links' },
  { key: 'attempts', label: 'Attempts' },
  { key: 'reports', label: 'Reports' },
];

type AdminListFilters = {
  q: string;
  company_id: string;
  status: 'ALL' | 'ACTIVE' | 'EXPIRED' | 'DISABLED' | 'IN_PROGRESS' | 'COMPLETED';
  variant_id: string;
  date_from: string;
  date_to: string;
};

const normalizeRequestParams = (params: Record<string, unknown>): Record<string, unknown> => {
  const normalized: Record<string, unknown> = {};
  for (const [key, rawValue] of Object.entries(params)) {
    if (rawValue === undefined || rawValue === null) continue;
    if (typeof rawValue === 'string' && rawValue.trim().length === 0) continue;
    normalized[key] = rawValue;
  }
  return normalized;
};

const toSearchParam = (value: string): string | null => {
  const text = normalizeString(value);
  return text.length > 0 ? text : null;
};

const getActiveAdminTab = (searchParams: URLSearchParams): AdminTab => {
  const raw = searchParams.get('tab');
  return raw && tabOrder.some((tab) => tab.key === raw) ? (raw as AdminTab) : 'import';
};

const parseListFilters = (searchParams: URLSearchParams, tab: string): AdminListFilters => ({
  q: searchParams.get(`${tab}_q`) || '',
  company_id: searchParams.get(`${tab}_company`) || searchParams.get(`${tab}_company_id`) || '',
  status: (searchParams.get(`${tab}_status`) as AdminListFilters['status']) || 'ALL',
  variant_id: searchParams.get(`${tab}_variant`) || searchParams.get(`${tab}_variant_id`) || '',
  date_from: searchParams.get(`${tab}_date_from`) || '',
  date_to: searchParams.get(`${tab}_date_to`) || '',
});

const readFiltersAsSearchParams = (
  searchParams: URLSearchParams,
  tab: 'links' | 'attempts' | 'reports'
): Partial<Record<`links_${string}` | `attempts_${string}` | `reports_${string}`, string>> => {
  const filters = parseListFilters(searchParams, tab);
  const entries: Array<[string, string]> = [
    [`${tab}_q`, filters.q],
    [`${tab}_company_id`, filters.company_id],
    [`${tab}_status`, filters.status],
    [`${tab}_variant_id`, filters.variant_id],
    [`${tab}_date_from`, filters.date_from],
    [`${tab}_date_to`, filters.date_to],
  ];
  const result: ReturnType<typeof readFiltersAsSearchParams> = {};
  for (const [key, value] of entries) {
    if (value) result[key] = value;
  }
  return result;
};

const getWalletId = (wallet: Wallet): string => wallet.id || wallet.company_id || '';

const makeRequestKey = (path: string, params: Record<string, unknown>): string => {
  const normalized = normalizeRequestParams(params);
  const pairs = Object.entries(normalized).sort(([a], [b]) => a.localeCompare(b));
  return `${path}|${JSON.stringify(pairs)}`;
};

const getRequestErrorMessage = (error: unknown, path: string): string => {
  const axiosError = error as {
    response?: { data?: { error?: { message?: string } } };
    code?: string;
    name?: string;
    message?: string;
  };
  return (
    axiosError.response?.data?.error?.message ||
    `Failed to load ${path.replace('/sim/admin/', '')}.`
  );
};

const isRequestCanceled = (error: unknown): boolean => {
  const axiosError = error as { code?: string; name?: string; message?: string };
  return (
    axiosError?.code === 'ERR_CANCELED' ||
    axiosError?.name === 'CanceledError' ||
    axiosError?.message === 'canceled'
  );
};

const mergePagedItems = <T,>(current: T[], nextItems: T[]): T[] => {
  const dedupedCurrent = new Map<string, T>();
  for (const item of current) {
    const id = (item as { id?: string }).id;
    if (!id) continue;
    dedupedCurrent.set(id, item);
  }

  const merged: T[] = [...dedupedCurrent.values()];
  const mergedIds = new Set(dedupedCurrent.keys());

  for (const nextItem of nextItems) {
    const id = (nextItem as { id?: string }).id;
    if (!id || mergedIds.has(id)) continue;
    merged.push(nextItem);
    mergedIds.add(id);
  }

  return merged;
};

const usePagedList = <T,>() => {
  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const requestIdRef = useRef(0);
  const activeRequestRef = useRef<AbortController | null>(null);
  const lastCompletedRequestRef = useRef<string | null>(null);

  const reset = useCallback(() => {
    setItems([]);
    setLoading(false);
    setError(null);
    setNextCursor(null);
    setHasMore(false);
    lastCompletedRequestRef.current = null;
    if (activeRequestRef.current) {
      activeRequestRef.current.abort();
      activeRequestRef.current = null;
    }
  }, []);

  const load = useCallback(
    async (path: string, params: Record<string, unknown>, append = false) => {
      const normalizedParams = normalizeRequestParams({ limit: 25, ...params });
      const requestKey = makeRequestKey(path, normalizedParams);
      if (!append && requestKey === lastCompletedRequestRef.current) {
        return;
      }

      if (activeRequestRef.current) {
        activeRequestRef.current.abort();
      }

      const controller = new AbortController();
      activeRequestRef.current = controller;

      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;
      setLoading(true);
      if (!append) {
        setError(null);
      }
      try {
      const response = await api.get<PagedResponse<T>>(path, {
        params: normalizedParams,
        signal: controller.signal,
      });
      if (requestId !== requestIdRef.current) return;
        const nextItems = response.data?.items || [];
        setItems((current) => (append ? mergePagedItems(current, nextItems) : nextItems));
        setNextCursor(response.data?.nextCursor ?? null);
        setHasMore(Boolean(response.data?.hasMore));
        if (!append) {
          lastCompletedRequestRef.current = requestKey;
        }
      } catch (error: any) {
        if (requestId !== requestIdRef.current) return;
        if (isRequestCanceled(error)) return;
        setError(getRequestErrorMessage(error, path));
      } finally {
        if (requestId === requestIdRef.current) {
          setLoading(false);
          if (activeRequestRef.current === controller) {
            activeRequestRef.current = null;
          }
        }
      }
    },
    []
  );

  const loadMore = useCallback(
    (path: string, baseParams: Record<string, unknown>) => {
      if (loading || error || !hasMore || !nextCursor) return;
      return load(path, { ...baseParams, cursor: nextCursor }, true);
    },
    [error, hasMore, nextCursor, load, loading]
  );

  return {
    items,
    loading,
    error,
    hasMore,
    nextCursor,
    load,
    loadMore,
    reset,
  };
};

const normalizeString = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : '';

const formatCandidateLabel = (candidate: AttemptItem['candidate']): string => {
  const name = candidate.name || 'Candidate';
  if (!candidate.id_last4) return `${name} (ID unknown)`;
  return `${name} (ID ••••${candidate.id_last4})`;
};

const formatImportLogLine = (entry: string | ImportLogLine): string => {
  if (typeof entry === 'string') return entry;
  const stream = entry.stream ? `[${entry.stream.toUpperCase()}] ` : '';
  const text = entry.text ?? '';
  const ts = entry.ts ? `[${entry.ts}] ` : '';
  return `${ts}${stream}${text}`.trim();
};

const parseImportLogs = (
  value: AdminImportResponse['logs']
): string[] => {
  if (!value) return [];
  if (typeof value === 'string') {
    return value
      .split('\\n')
      .map((line) => line.trimEnd())
      .filter((line) => line.length > 0);
  }
  if (Array.isArray(value)) {
    return value.map(formatImportLogLine).filter((line) => line.length > 0);
  }
  return [];
};

const readClipboard = async (text: string) => {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const fallback = document.createElement('textarea');
  fallback.value = text;
  fallback.setAttribute('readonly', 'true');
  fallback.style.position = 'absolute';
  fallback.style.left = '-9999px';
  document.body.appendChild(fallback);
  fallback.select();
  document.execCommand('copy');
  document.body.removeChild(fallback);
};

const EmployerAdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = getActiveAdminTab(searchParams);
  const [adminAuthorized, setAdminAuthorized] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);

  const [wallets, setWallets] = useState<Wallet[]>([]);
  const [walletLoading, setWalletLoading] = useState(false);
  const [walletError, setWalletError] = useState<string | null>(null);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
  const [topupAmount, setTopupAmount] = useState('');
  const [topupReason, setTopupReason] = useState('');
  const [topupReference, setTopupReference] = useState('');
  const [topupSubmitting, setTopupSubmitting] = useState(false);
  const [removeSubmitting, setRemoveSubmitting] = useState(false);
  const [buildRequests, setBuildRequests] = useState<CommerceBuildRequest[]>([]);
  const [buildRequestsLoading, setBuildRequestsLoading] = useState(false);
  const [buildRequestsError, setBuildRequestsError] = useState<string | null>(null);
  const [buildRequestUpdatingId, setBuildRequestUpdatingId] = useState<string | null>(null);
  const [payFastLiveTestStatus, setPayFastLiveTestStatus] = useState<PayFastLiveTestStatus | null>(null);
  const [payFastLiveTestError, setPayFastLiveTestError] = useState<string | null>(null);
  const [payFastLiveTestSubmitting, setPayFastLiveTestSubmitting] = useState<'app' | 'landing' | null>(null);

  const [variants, setVariants] = useState<VariantItem[]>([]);
  const [variantsLoading, setVariantsLoading] = useState(false);
  const [setGoldMessage, setSetGoldMessage] = useState<string | null>(null);
  const [simulations, setSimulations] = useState<SimulationAdminItem[]>([]);
  const [simulationsLoading, setSimulationsLoading] = useState(false);
  const [simulationsError, setSimulationsError] = useState<string | null>(null);
  const [selectedSimulationCode, setSelectedSimulationCode] = useState<string>('');
  const [simulationVersions, setSimulationVersions] = useState<SimulationVersionItem[]>([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [versionsError, setVersionsError] = useState<string | null>(null);
  const [simulationAccessRows, setSimulationAccessRows] = useState<SimulationAccessItem[]>([]);
  const [accessLoading, setAccessLoading] = useState(false);
  const [accessError, setAccessError] = useState<string | null>(null);
  const [simulationUpdatingKey, setSimulationUpdatingKey] = useState<string | null>(null);
  const [selectedSimulationPanel, setSelectedSimulationPanel] = useState<'versions' | 'access' | null>(null);
  const [simulationCardErrors, setSimulationCardErrors] = useState<Record<string, string>>({});
  const [simulationRetryPayloads, setSimulationRetryPayloads] = useState<Record<string, Record<string, unknown>>>({});
  const [importSimulationCode, setImportSimulationCode] = useState('');
  const [importSimulationVersion, setImportSimulationVersion] = useState('');

  type ImportRunState = 'idle' | 'running' | 'success' | 'failed' | 'cancelled';
  const [importRunState, setImportRunState] = useState<ImportRunState>('idle');
  const [legacyIb, setLegacyIb] = useState(true);
  const [importPath, setImportPath] = useState('');
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importLog, setImportLog] = useState<string[]>([]);
  const [importSummary, setImportSummary] = useState<AdminImportResponse['summary'] | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [importMode, setImportMode] = useState<'dry-run' | 'commit'>('dry-run');
  const [preflightRunning, setPreflightRunning] = useState(false);
  const [preflightError, setPreflightError] = useState<string | null>(null);
  const [preflightResult, setPreflightResult] = useState<AdminImportPreflightResponse | null>(null);
  const importAbortRef = useRef<AbortController | null>(null);
  const preflightAbortRef = useRef<AbortController | null>(null);
  const versionsAbortRef = useRef<AbortController | null>(null);
  const accessAbortRef = useRef<AbortController | null>(null);
  const versionsRequestIdRef = useRef(0);
  const accessRequestIdRef = useRef(0);
  const simulationCardRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const links = usePagedList<LinkItem>();
  const attempts = usePagedList<AttemptItem>();
  const reports = usePagedList<ReportItem>();

  const currentFilters: {
    links: AdminListFilters;
    attempts: AdminListFilters;
    reports: AdminListFilters;
  } = useMemo(
    () => ({
      links: parseListFilters(searchParams, 'links'),
      attempts: parseListFilters(searchParams, 'attempts'),
      reports: parseListFilters(searchParams, 'reports'),
    }),
    [searchParams]
  );

  const visibleAdminTabs = useMemo(
    () => tabOrder.filter((tab) => tab.key !== 'payfastTest' || payFastLiveTestStatus?.enabled),
    [payFastLiveTestStatus?.enabled]
  );

  const dateFromMs = (value: string): string | undefined => {
    const text = normalizeString(value);
    if (!text) return undefined;
    const date = new Date(`${text}T00:00:00.000Z`);
    return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
  };

  const dateToMs = (value: string): string | undefined => {
    const text = normalizeString(value);
    if (!text) return undefined;
    const date = new Date(`${text}T23:59:59.999Z`);
    return Number.isNaN(date.getTime()) ? undefined : date.toISOString();
  };

  const setFilter = useCallback(
    (tab: 'links' | 'attempts' | 'reports', field: keyof AdminListFilters, value: string) => {
      const nextParams = new URLSearchParams(searchParams);
      const namespaced = `${tab}_${field}`;
      const normalized = toSearchParam(value);
      if (normalized === null) {
        nextParams.delete(namespaced);
      } else {
        nextParams.set(namespaced, normalized);
      }
      nextParams.set('tab', activeTab);
      setSearchParams(nextParams, { replace: true });
    },
    [activeTab, searchParams, setSearchParams]
  );

  const clearFilters = useCallback(
    (tab: 'links' | 'attempts' | 'reports') => {
      const nextParams = new URLSearchParams(searchParams);
      const base = readFiltersAsSearchParams(searchParams, tab);
      for (const key of Object.keys(base) as Array<`${'links' | 'attempts' | 'reports'}_${string}`>) {
        nextParams.delete(key);
      }
      nextParams.set('tab', activeTab);
      setSearchParams(nextParams, { replace: true });
    },
    [activeTab, searchParams, setSearchParams]
  );

  const goToTab = useCallback(
    (nextTab: AdminTab) => {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set('tab', nextTab);
      setSearchParams(nextParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const linkListParams = useMemo(
    () => ({
      q: normalizeString(currentFilters.links.q) || undefined,
      company_id: normalizeString(currentFilters.links.company_id) || undefined,
      status: normalizeString(currentFilters.links.status) || undefined,
      date_from: dateFromMs(currentFilters.links.date_from),
      date_to: dateToMs(currentFilters.links.date_to),
      variant_id: normalizeString(currentFilters.links.variant_id) || undefined,
    }),
    [currentFilters]
  );

  const attemptListParams = useMemo(
    () => ({
      q: normalizeString(currentFilters.attempts.q) || undefined,
      company_id: normalizeString(currentFilters.attempts.company_id) || undefined,
      variant_id: normalizeString(currentFilters.attempts.variant_id) || undefined,
      status: normalizeString(currentFilters.attempts.status) || undefined,
      date_from: dateFromMs(currentFilters.attempts.date_from),
      date_to: dateToMs(currentFilters.attempts.date_to),
    }),
    [currentFilters]
  );

  const reportListParams = useMemo(
    () => ({
      q: normalizeString(currentFilters.reports.q) || undefined,
      company_id: normalizeString(currentFilters.reports.company_id) || undefined,
      variant_id: normalizeString(currentFilters.reports.variant_id) || undefined,
      date_from: dateFromMs(currentFilters.reports.date_from),
      date_to: dateToMs(currentFilters.reports.date_to),
    }),
    [currentFilters]
  );

  const adminOnlyRequest = useCallback(async () => {
    try {
      const response = await api.get<{ companies: Wallet[] }>('/sim/admin/companies');
      setWallets(response.data?.companies || []);
      setWalletError(null);
      setAdminAuthorized(true);
    } catch (error: any) {
      setAdminAuthorized(false);
      setWalletError('Not authorized to access admin dashboard.');
    } finally {
      setCheckingAuth(false);
    }
  }, []);

  useEffect(() => {
    adminOnlyRequest();
  }, [adminOnlyRequest]);

  const refreshWallets = useCallback(async () => {
    if (!adminAuthorized) return;
    setWalletLoading(true);
    try {
      const response = await api.get<{ companies: Wallet[] }>('/sim/admin/companies');
      const list = response.data?.companies || [];
      setWallets(list);
      setWalletError(null);
    } catch (error: any) {
      setWalletError('Unable to load companies.');
      toast.error('Unable to load companies.');
    } finally {
      setWalletLoading(false);
    }
  }, [adminAuthorized]);

  const refreshVariants = useCallback(async () => {
    setVariantsLoading(true);
    setSetGoldMessage(null);
    try {
      const response = await api.get<{ variants: VariantItem[] }>('/sim/admin/variants');
      setVariants(response.data.variants || []);
    } catch (error: any) {
      toast.error('Unable to load variants.');
      setVariants([]);
    } finally {
      setVariantsLoading(false);
    }
  }, []);

  const refreshSimulations = useCallback(async () => {
    if (!adminAuthorized) return;
    setSimulationsLoading(true);
    setSimulationsError(null);
    try {
      const response = await api.get<{ simulations: SimulationAdminItem[] }>('/sim/admin/simulations');
      setSimulations(response.data?.simulations || []);
    } catch (error: any) {
      const message = error?.response?.data?.error?.message || 'Unable to load simulations.';
      setSimulationsError(message);
      setSimulations([]);
    } finally {
      setSimulationsLoading(false);
    }
  }, [adminAuthorized]);

  const openSimulationPanel = useCallback((simulationCode: string, panel: 'versions' | 'access') => {
    setSelectedSimulationCode(simulationCode);
    setSelectedSimulationPanel(panel);
    requestAnimationFrame(() => {
      const card = simulationCardRefs.current[simulationCode];
      if (card && typeof card.scrollIntoView === 'function') {
        card.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }
    });
  }, []);

  const loadSimulationVersions = useCallback(async (simulationCode: string) => {
    if (!simulationCode) return;
    if (versionsAbortRef.current) {
      versionsAbortRef.current.abort();
      versionsAbortRef.current = null;
    }
    const controller = new AbortController();
    versionsAbortRef.current = controller;
    const requestId = versionsRequestIdRef.current + 1;
    versionsRequestIdRef.current = requestId;

    setVersionsLoading(true);
    setVersionsError(null);
    openSimulationPanel(simulationCode, 'versions');
    try {
      const response = await api.get<{ versions: SimulationVersionItem[] }>(
        `/sim/admin/simulations/${encodeURIComponent(simulationCode)}/versions`,
        {
          signal: controller.signal,
        }
      );
      if (requestId !== versionsRequestIdRef.current) return;
      setSimulationVersions(response.data?.versions || []);
    } catch (error: any) {
      if (requestId !== versionsRequestIdRef.current || isRequestCanceled(error)) return;
      setSimulationVersions([]);
      const message = error?.response?.data?.error?.message || 'Unable to load simulation versions.';
      setVersionsError(message);
    } finally {
      if (requestId === versionsRequestIdRef.current) {
        setVersionsLoading(false);
        if (versionsAbortRef.current === controller) {
          versionsAbortRef.current = null;
        }
      }
    }
  }, [openSimulationPanel]);

  const loadSimulationAccess = useCallback(async (simulationCode: string) => {
    if (!simulationCode) return;
    if (accessAbortRef.current) {
      accessAbortRef.current.abort();
      accessAbortRef.current = null;
    }
    const controller = new AbortController();
    accessAbortRef.current = controller;
    const requestId = accessRequestIdRef.current + 1;
    accessRequestIdRef.current = requestId;

    setAccessLoading(true);
    setAccessError(null);
    openSimulationPanel(simulationCode, 'access');
    try {
      const response = await api.get<{ access: SimulationAccessItem[] }>(
        `/sim/admin/simulations/${encodeURIComponent(simulationCode)}/access`,
        {
          signal: controller.signal,
        }
      );
      if (requestId !== accessRequestIdRef.current) return;
      setSimulationAccessRows(response.data?.access || []);
    } catch (error: any) {
      if (requestId !== accessRequestIdRef.current || isRequestCanceled(error)) return;
      setSimulationAccessRows([]);
      const message = error?.response?.data?.error?.message || 'Unable to load simulation access controls.';
      setAccessError(message);
    } finally {
      if (requestId === accessRequestIdRef.current) {
        setAccessLoading(false);
        if (accessAbortRef.current === controller) {
          accessAbortRef.current = null;
        }
      }
    }
  }, [openSimulationPanel]);

  const patchSimulationSettings = useCallback(
    async (simulationCode: string, payload: Record<string, unknown>) => {
      if (simulationUpdatingKey === simulationCode) return;
      setSimulationUpdatingKey(simulationCode);
      setSimulationCardErrors((current) => {
        const next = { ...current };
        delete next[simulationCode];
        return next;
      });
      setSimulationRetryPayloads((current) => {
        const next = { ...current };
        delete next[simulationCode];
        return next;
      });
      try {
        const response = await api.patch<{ simulation?: SimulationAdminItem }>(
          `/sim/admin/simulations/${encodeURIComponent(simulationCode)}`,
          payload
        );
        const updatedSimulation = response.data?.simulation;
        if (updatedSimulation) {
          setSimulations((current) =>
            current.map((simulation) =>
              simulation.simulation_code === simulationCode ? updatedSimulation : simulation
            )
          );
        } else {
          await refreshSimulations();
        }
        if (
          selectedSimulationCode === simulationCode &&
          selectedSimulationPanel === 'access' &&
          ((payload.visibility as string | undefined)?.toUpperCase() || '') === 'PUBLIC'
        ) {
          setSelectedSimulationPanel(null);
          setSimulationAccessRows([]);
        }
        if (
          selectedSimulationCode === simulationCode &&
          selectedSimulationPanel === 'access' &&
          simulationAccessRows.length > 0
        ) {
          await loadSimulationAccess(simulationCode);
        }
      } catch (error: any) {
        const message = error?.response?.data?.error?.message || 'Unable to update simulation settings.';
        setSimulationCardErrors((current) => ({
          ...current,
          [simulationCode]: `Could not save setting: ${message}`,
        }));
        setSimulationRetryPayloads((current) => ({
          ...current,
          [simulationCode]: payload,
        }));
      } finally {
        setSimulationUpdatingKey(null);
      }
    },
    [
      loadSimulationAccess,
      refreshSimulations,
      selectedSimulationCode,
      selectedSimulationPanel,
      simulationAccessRows.length,
      simulationUpdatingKey,
    ]
  );

  const makeSimulationVersionActive = useCallback(
    async (simulationCode: string, versionId: string) => {
      setSimulationUpdatingKey(`${simulationCode}:${versionId}`);
      try {
        await api.post(`/sim/admin/simulations/${encodeURIComponent(simulationCode)}/set-active`, {
          version_id: versionId,
        });
        await Promise.all([refreshSimulations(), loadSimulationVersions(simulationCode)]);
        toast.success('Active version updated');
      } catch (error: any) {
        const message = error?.response?.data?.error?.message || 'Unable to set active version.';
        toast.error(message);
      } finally {
        setSimulationUpdatingKey(null);
      }
    },
    [loadSimulationVersions, refreshSimulations]
  );

  const archiveSimulationVersion = useCallback(
    async (simulationCode: string, versionId: string) => {
      setSimulationUpdatingKey(`${simulationCode}:archive:${versionId}`);
      try {
        await api.delete(`/sim/admin/simulations/${encodeURIComponent(simulationCode)}/versions/${encodeURIComponent(versionId)}`);
        await Promise.all([refreshSimulations(), loadSimulationVersions(simulationCode)]);
        toast.success('Version archived');
      } catch (error: any) {
        const message = error?.response?.data?.error?.message || 'Unable to archive version.';
        setVersionsError(message);
      } finally {
        setSimulationUpdatingKey(null);
      }
    },
    [loadSimulationVersions, refreshSimulations]
  );

  const archiveSimulationFamily = useCallback(
    async (simulationCode: string) => {
      setSimulationUpdatingKey(simulationCode);
      setSimulationCardErrors((current) => {
        const next = { ...current };
        delete next[simulationCode];
        return next;
      });
      try {
        const response = await api.post<{ simulation?: SimulationAdminItem }>(
          `/sim/admin/simulations/${encodeURIComponent(simulationCode)}/archive`
        );
        const updatedSimulation = response.data?.simulation;
        if (updatedSimulation) {
          setSimulations((current) =>
            current.map((simulation) =>
              simulation.simulation_code === simulationCode ? updatedSimulation : simulation
            )
          );
        } else {
          await refreshSimulations();
        }
      } catch (error: any) {
        const message = error?.response?.data?.error?.message || 'Unable to archive simulation.';
        setSimulationCardErrors((current) => ({
          ...current,
          [simulationCode]: `Could not save setting: ${message}`,
        }));
      } finally {
        setSimulationUpdatingKey(null);
      }
    },
    [refreshSimulations]
  );

  const updateSimulationCompanyAccess = useCallback(
    async (simulationCode: string, companyId: string, enabled: boolean) => {
      setSimulationUpdatingKey(`${simulationCode}:${companyId}`);
      try {
        await api.patch(`/sim/admin/simulations/${encodeURIComponent(simulationCode)}/access`, {
          company_id: companyId,
          enabled,
        });
        await loadSimulationAccess(simulationCode);
      } catch (error: any) {
        const message = error?.response?.data?.error?.message || 'Unable to update company access.';
        toast.error(message);
      } finally {
        setSimulationUpdatingKey(null);
      }
    },
    [loadSimulationAccess]
  );

  const variantsLoadedRef = useRef(false);
  const simulationsLoadedRef = useRef(false);

  useEffect(() => {
    if (!adminAuthorized) return;
    if (!variantsLoadedRef.current) {
      void refreshVariants();
      variantsLoadedRef.current = true;
    }
  }, [adminAuthorized, refreshVariants]);

  useEffect(() => {
    if (!adminAuthorized) return;
    if (activeTab !== 'simulations') return;
    if (simulationsLoadedRef.current) return;
    void refreshSimulations();
    simulationsLoadedRef.current = true;
  }, [activeTab, adminAuthorized, refreshSimulations]);

  const canTopup = useMemo(() => {
    const amount = Number(topupAmount);
    return (
      Boolean(selectedCompanyId) &&
      Number.isInteger(amount) &&
      amount > 0 &&
      !topupSubmitting
    );
  }, [selectedCompanyId, topupAmount, topupSubmitting]);

  const selectedWallet = useMemo(
    () => wallets.find((wallet) => getWalletId(wallet) === selectedCompanyId) || null,
    [wallets, selectedCompanyId]
  );

  const canRemoveCredits = useMemo(() => {
    const amount = Number(topupAmount);
    return (
      Boolean(selectedCompanyId) &&
      Number.isInteger(amount) &&
      amount < 0 &&
      !removeSubmitting
    );
  }, [selectedCompanyId, topupAmount, removeSubmitting]);

  const selectedCompanyExists = useMemo(
    () => wallets.some((wallet) => getWalletId(wallet) === selectedCompanyId),
    [wallets, selectedCompanyId]
  );

  useEffect(() => {
    if (!selectedCompanyId && wallets.length > 0) {
      setSelectedCompanyId(getWalletId(wallets[0]));
    }
    if (selectedCompanyId && !selectedCompanyExists) {
      setSelectedCompanyId(wallets[0] ? getWalletId(wallets[0]) : '');
    }
  }, [wallets, selectedCompanyExists, selectedCompanyId]);

  useEffect(() => {
    if (!adminAuthorized) return;
    void refreshWallets();
  }, [adminAuthorized, refreshWallets]);

  const linkListKey = useMemo(() => makeRequestKey('/sim/admin/links', linkListParams), [linkListParams]);
  const attemptListKey = useMemo(() => makeRequestKey('/sim/admin/attempts', attemptListParams), [attemptListParams]);
  const reportListKey = useMemo(() => makeRequestKey('/sim/admin/reports', reportListParams), [reportListParams]);

  const formatBuildRequestStatusLabel = useCallback((status: CommerceBuildRequestStatus) => {
    if (status === 'PAID' || status === 'SCOPING') return 'Allocated for build';
    if (status === 'IN_BUILD') return 'Final checks';
    if (status === 'DELIVERED') return 'Deployed to dashboard';
    if (status === 'INVOICE_REQUESTED') return 'Invoice requested';
    if (status === 'AWAITING_BILLING') return 'Awaiting invoice details';
    if (status === 'PENDING') return 'Pending payment';
    if (status === 'CANCELLED') return 'Cancelled';
    return status;
  }, []);

  const refreshBuildRequests = useCallback(async () => {
    if (!adminAuthorized) return;
    setBuildRequestsLoading(true);
    setBuildRequestsError(null);
    try {
      const response = await api.get<{ buildRequests: CommerceBuildRequest[] }>('/commerce/admin/build-requests');
      setBuildRequests(response.data?.buildRequests || []);
    } catch (error: any) {
      const message = error?.response?.data?.error?.message || 'Unable to load build requests.';
      setBuildRequestsError(message);
      setBuildRequests([]);
    } finally {
      setBuildRequestsLoading(false);
    }
  }, [adminAuthorized]);

  const updateBuildRequestStatus = useCallback(
    async (buildRequestId: string, status: Extract<CommerceBuildRequestStatus, 'PAID' | 'IN_BUILD' | 'DELIVERED'>) => {
      setBuildRequestUpdatingId(buildRequestId);
      setBuildRequestsError(null);
      try {
        const response = await api.patch<{ buildRequest: CommerceBuildRequest }>(
          `/commerce/admin/build-requests/${buildRequestId}/status`,
          { status }
        );
        const updated = response.data?.buildRequest;
        if (updated) {
          setBuildRequests((current) =>
            current.map((request) => (request.id === updated.id ? updated : request))
          );
        } else {
          await refreshBuildRequests();
        }
        toast.success('Build request status updated.');
      } catch (error: any) {
        const message = error?.response?.data?.error?.message || 'Unable to update build request.';
        setBuildRequestsError(message);
        toast.error(message);
      } finally {
        setBuildRequestUpdatingId(null);
      }
    },
    [refreshBuildRequests]
  );

  const refreshPayFastLiveTestStatus = useCallback(async () => {
    if (!adminAuthorized) return;
    try {
      const status = await fetchPayFastLiveTestStatus();
      setPayFastLiveTestStatus(status);
      setPayFastLiveTestError(null);
    } catch (error: any) {
      setPayFastLiveTestStatus({ enabled: false, appOfferingCode: '', landingOfferingCode: '', amountZar: '' });
      if (activeTab === 'payfastTest') {
        setPayFastLiveTestError(
          error?.response?.data?.error?.message || 'Unable to load PayFast live-test status.'
        );
      }
    }
  }, [activeTab, adminAuthorized]);

  const runPayFastLiveTest = useCallback(async (kind: 'app' | 'landing') => {
    setPayFastLiveTestSubmitting(kind);
    setPayFastLiveTestError(null);
    try {
      const result = kind === 'app' ? await createPayFastLiveTestAppOrder() : await createPayFastLiveTestLandingOrder();
      if (!result.payfastUrl || !result.formFields) {
        throw new Error('PayFast checkout data was not returned.');
      }
      rememberPendingCommerceOrder(result.orderId);
      submitPayFastHostedPayment(result.payfastUrl, result.formFields);
    } catch (error: any) {
      const message = error?.response?.data?.error?.message || error?.message || 'Unable to start PayFast live test.';
      setPayFastLiveTestError(message);
      toast.error(message);
      setPayFastLiveTestSubmitting(null);
    }
  }, []);

  useEffect(() => {
    if (!adminAuthorized) return;
    void refreshPayFastLiveTestStatus();
  }, [adminAuthorized, refreshPayFastLiveTestStatus]);

  useEffect(() => {
    if (!adminAuthorized) return;
    if (activeTab === 'links') {
      links.reset();
      void links.load('/sim/admin/links', linkListParams);
    }
    if (activeTab === 'attempts') {
      attempts.reset();
      void attempts.load('/sim/admin/attempts', attemptListParams);
    }
    if (activeTab === 'reports') {
      reports.reset();
      void reports.load('/sim/admin/reports', reportListParams);
    }
    if (activeTab === 'buildRequests') {
      void refreshBuildRequests();
    }
  }, [
    activeTab,
    adminAuthorized,
    refreshBuildRequests,
    linkListKey,
    attemptListKey,
    reportListKey,
    links.load,
    attempts.load,
    reports.load,
    links.reset,
    attempts.reset,
    reports.reset,
  ]);

  const reloadLinks = useCallback(() => {
    if (!adminAuthorized) return;
    links.reset();
    void links.load('/sim/admin/links', linkListParams);
  }, [adminAuthorized, links, linkListParams]);

  const reloadAttempts = useCallback(() => {
    if (!adminAuthorized) return;
    attempts.reset();
    void attempts.load('/sim/admin/attempts', attemptListParams);
  }, [adminAuthorized, attempts, attemptListParams]);

  const reloadReports = useCallback(() => {
    if (!adminAuthorized) return;
    reports.reset();
    void reports.load('/sim/admin/reports', reportListParams);
  }, [adminAuthorized, reports, reportListParams]);

  const submitTopup = async () => {
    if (!selectedCompanyId || !selectedCompanyExists) {
      setWalletError('Select a company first.');
      return;
    }
    const amount = Number(topupAmount);
    if (!Number.isInteger(amount) || amount <= 0) {
      setWalletError('Enter a positive integer amount.');
      return;
    }
    setTopupSubmitting(true);
    setWalletError(null);
    try {
      await api.post('/sim/wallet/admin-credit', {
        company_id: selectedCompanyId,
        amount,
        type: 'CREDIT_ADJUSTMENT',
        reason: topupReason || undefined,
        reference: topupReference || undefined,
      });
      await refreshWallets();
      setTopupAmount('');
      setTopupReason('');
      setTopupReference('');
      toast.success('Credits added.');
    } catch (error: any) {
      const message = error?.response?.data?.error?.message || 'Failed to add credits.';
      setWalletError(message);
    } finally {
      setTopupSubmitting(false);
    }
  };

  const submitRemoveCredits = async () => {
    if (!selectedCompanyId || !selectedCompanyExists || !selectedWallet) {
      setWalletError('Select a company first.');
      return;
    }
    const amount = Number(topupAmount);
    if (!Number.isInteger(amount) || amount >= 0) {
      setWalletError('Enter a valid number of credits.');
      return;
    }
    const amountToRemove = Math.abs(amount);
    if (amountToRemove > selectedWallet.wallet.credits_available) {
      setWalletError('Cannot remove more credits than the company currently has.');
      return;
    }
    const confirmed = window.confirm(`Remove ${amountToRemove} credits from ${selectedWallet.name}?`);
    if (!confirmed) return;

    setRemoveSubmitting(true);
    setWalletError(null);
    try {
      await api.post('/sim/wallet/admin-remove-credit', {
        company_id: selectedCompanyId,
        amount: amountToRemove,
        reason: 'Admin manual credit removal',
      });
      await refreshWallets();
      setTopupAmount('');
      toast.success('Credits removed successfully.');
    } catch (error: any) {
      const message = error?.response?.data?.error?.message || 'Failed to remove credits.';
      setWalletError(message);
    } finally {
      setRemoveSubmitting(false);
    }
  };

  const setGoldDemo = async (variantId: string) => {
    setSetGoldMessage(null);
    try {
      await api.post(`/sim/admin/variants/${variantId}/set-gold`);
      await refreshVariants();
      setSetGoldMessage('Gold variant updated.');
    } catch (_error) {
      setSetGoldMessage('Unable to update gold variant.');
    }
  };

  const startImport = async (mode: 'dry-run' | 'commit') => {
    if (!importPath.trim() && !importFile) {
      setImportError('Provide a file upload or file path.');
      return;
    }

    if (importAbortRef.current) {
      importAbortRef.current.abort();
      importAbortRef.current = null;
    }
    const controller = new AbortController();
    importAbortRef.current = controller;

    setImportMode(mode);
    setImportRunState('running');
    setImportError(null);
    setImportSummary(null);
    setImportLog([]);

    const finishImport = (status: ImportRunState) => {
      setImportRunState(status);
      if (importAbortRef.current === controller) {
        importAbortRef.current = null;
      }
    };

    try {
      const body = new FormData();
      body.append('legacyIb', String(legacyIb));
      body.append('dryRun', String(mode === 'dry-run'));
      body.append('commit', String(mode === 'commit'));
      if (importPath.trim()) body.append('filePath', importPath.trim());
      if (importFile) body.append('file', importFile);
      if (importSimulationCode.trim()) body.append('simulationCode', importSimulationCode.trim());
      if (importSimulationVersion.trim()) body.append('simulationVersion', importSimulationVersion.trim());
      const response = await api.post<AdminImportResponse>('/sim/admin/import', body, {
        headers: { 'Content-Type': 'multipart/form-data' },
        signal: controller.signal,
      });
      const payload = response.data;
      setImportLog(parseImportLogs(payload.logs));
      setImportSummary(payload.summary || null);
      if (!payload.ok) {
        const detail = payload.error?.details?.[0]?.message;
        setImportError(detail || payload.error?.message || 'Import failed.');
        finishImport('failed');
        return;
      }
      finishImport('success');
      toast.success('Simulation import complete.');
    } catch (error: any) {
      if (isRequestCanceled(error)) {
        finishImport('cancelled');
        setImportError('Import cancelled.');
        return;
      }
      const data = error?.response?.data;
      const message =
        data?.error?.details?.[0]?.message || data?.error?.message || 'Import request failed.';
      setImportError(message);
      setImportLog(parseImportLogs(data?.logs));
      finishImport('failed');
    }
  };

  const cancelImport = () => {
    if (importAbortRef.current) {
      importAbortRef.current.abort();
      importAbortRef.current = null;
      setImportRunState('cancelled');
      setImportError('Import cancelled.');
    }
  };

  const runPreflightValidate = async () => {
    if (!importPath.trim() && !importFile) {
      setPreflightError('Provide a file upload or file path.');
      return;
    }

    if (preflightAbortRef.current) {
      preflightAbortRef.current.abort();
      preflightAbortRef.current = null;
    }
    const controller = new AbortController();
    preflightAbortRef.current = controller;

    setPreflightRunning(true);
    setPreflightError(null);
    setPreflightResult(null);

    try {
      const body = new FormData();
      body.append('legacyIb', String(legacyIb));
      if (importPath.trim()) body.append('filePath', importPath.trim());
      if (importFile) body.append('file', importFile);
      if (importSimulationCode.trim()) body.append('simulationCode', importSimulationCode.trim());
      if (importSimulationVersion.trim()) body.append('simulationVersion', importSimulationVersion.trim());

      const response = await api.post<AdminImportPreflightResponse>('/sim/admin/import/preflight', body, {
        headers: { 'Content-Type': 'multipart/form-data' },
        signal: controller.signal,
      });
      setPreflightResult(response.data);
      if (!response.data.ok) {
        setPreflightError('Preflight found validation issues.');
      } else {
        toast.success('Preflight complete.');
      }
    } catch (error: any) {
      if (isRequestCanceled(error)) {
        setPreflightError('Preflight cancelled.');
        return;
      }
      const message =
        error?.response?.data?.error?.message || 'Preflight request failed.';
      setPreflightError(message);
    } finally {
      if (preflightAbortRef.current === controller) {
        preflightAbortRef.current = null;
      }
      setPreflightRunning(false);
    }
  };

  const copyImportLogs = async () => {
    const text = importLog.join('\n');
    await readClipboard(text);
    toast.success('Import logs copied.');
  };

  const formatDateTime = useMemo(() => (value: string | null) => {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString();
  }, []);

  const getLinkStatusPillClass = useCallback((status: LinkItem['status']) => {
    if (status === 'ACTIVE') return 'border-[#8fd5c8] bg-[#e6f7f3] text-[#0f766e]';
    if (status === 'EXPIRED') return 'border-[#d7c1ab] bg-[#f6efe8] text-[#8a5a2b]';
    return 'border-slate-300 bg-slate-100 text-slate-700';
  }, []);

  const formatLinkStatusLabel = useCallback((status: LinkItem['status']) => {
    if (status === 'ACTIVE') return 'Active';
    if (status === 'EXPIRED') return 'Expired';
    return 'Disabled';
  }, []);

  const variantCountBySimulationId = useMemo(() => {
    const counts = new Map<string, number>();
    for (const variant of variants) {
      const simulationId = variant.simulation?.id;
      if (!simulationId) continue;
      counts.set(simulationId, (counts.get(simulationId) || 0) + 1);
    }
    return counts;
  }, [variants]);

  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-surface-page">
        <EmployerHeader />
        <SimShell
          fixedAction={
            <SimButton onClick={() => navigate('/employer/dashboard')}>
              Back
            </SimButton>
          }
        >
          <SimCard>
            <SimBody>Checking admin authorization...</SimBody>
          </SimCard>
        </SimShell>
      </div>
    );
  }

  if (!adminAuthorized) {
    return (
      <div className="min-h-screen bg-surface-page">
        <EmployerHeader />
        <SimShell
          fixedAction={
            <SimButton onClick={() => navigate('/employer/dashboard')}>
              Back
            </SimButton>
          }
        >
          <SimCard className="space-y-4">
            <SimLabel>Access restricted</SimLabel>
            <div className="text-txt-secondary">You do not have access to simulation admin tools.</div>
          </SimCard>
        </SimShell>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-page">
      <EmployerHeader />
      <SimShell
        fixedAction={
          <SimButton onClick={() => navigate('/employer/dashboard')}>
            Back
          </SimButton>
        }
        header={
          <div className="space-y-1">
            <SimLabel className="text-emerald-300">Admin</SimLabel>
            <SimTitle>Simulation Admin Dashboard</SimTitle>
            <SimBody>Manage simulation links, attempts, reports, variants, and import jobs.</SimBody>
          </div>
        }
      >
      <div className="flex flex-wrap gap-2">
        {visibleAdminTabs.map((tab) => (
            <SimButton
              key={tab.key}
              variant={activeTab === tab.key ? 'primary' : 'secondary'}
              onClick={() => goToTab(tab.key)}
            >
              {tab.label}
            </SimButton>
        ))}
      </div>

      <SimCard className="space-y-4">
        {activeTab === 'import' ? (
          <section className="space-y-4">
            <SimLabel>Import</SimLabel>
            <div className="rounded-input border border-line bg-surface-card/40 px-3 py-2 text-sm">
              <div className="font-semibold">Run Output</div>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                <span
                  className="rounded-full bg-brand-600 px-3 py-1 text-black"
                >
                  {importRunState === 'running'
                    ? 'Running'
                    : importRunState === 'success'
                      ? 'Success'
                      : importRunState === 'failed'
                        ? 'Failed'
                        : importRunState === 'cancelled'
                          ? 'Cancelled'
                          : 'Idle'}
                </span>
                {importMode ? <span className="rounded-full bg-brand-600 px-3 py-1 text-black">Mode: {importMode}</span> : null}
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm text-txt-secondary">Server workbook path (optional)</label>
              <input
                value={importPath}
                onChange={(event) => setImportPath(event.target.value)}
                className="w-full rounded-input border border-line bg-surface-page/60 px-4 py-3 text-txt-primary"
                placeholder="/path/to/import.xlsx"
              />
              <label className="text-sm text-txt-secondary">Or upload a file</label>
              <input
                type="file"
                accept=".xlsx,.xlsm,.xls"
                onChange={(event) => setImportFile(event.target.files?.[0] ?? null)}
                className="text-sm text-txt-primary"
              />
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <label className="text-sm text-txt-secondary">Simulation Code (optional)</label>
                <input
                  value={importSimulationCode}
                  onChange={(event) => setImportSimulationCode(event.target.value)}
                  className="mt-2 w-full rounded-input border border-line bg-surface-page/60 px-4 py-3 text-txt-primary"
                  placeholder="IB-RPF"
                />
              </div>
              <div>
                <label className="text-sm text-txt-secondary">Version Label (optional)</label>
                <input
                  value={importSimulationVersion}
                  onChange={(event) => setImportSimulationVersion(event.target.value)}
                  className="mt-2 w-full rounded-input border border-line bg-surface-page/60 px-4 py-3 text-txt-primary"
                  placeholder="v2"
                />
              </div>
            </div>
            <label className="inline-flex items-center gap-2 text-sm text-txt-secondary">
              <input
                type="checkbox"
                checked={legacyIb}
                onChange={(event) => setLegacyIb(event.target.checked)}
              />
              Flexible import mode (recommended)
            </label>
            <p className="text-xs text-txt-muted">
              Tolerant on formatting, strict on correctness: accepts minor workbook formatting differences, but will still
              block missing answer keys, missing scoring tolerances, and time-limit violations.
            </p>
            <div className="flex flex-wrap gap-3">
              <SimButton onClick={() => startImport('dry-run')} disabled={importRunState === 'running'}>
                {importRunState === 'running' ? 'Running…' : 'Run dry-run'}
              </SimButton>
              <SimButton onClick={() => startImport('commit')} disabled={importRunState === 'running'} variant="secondary">
                {importRunState === 'running' ? 'Running…' : 'Run import'}
              </SimButton>
              <SimButton onClick={runPreflightValidate} disabled={importRunState === 'running' || preflightRunning} variant="secondary">
                {preflightRunning ? 'Validating…' : 'Run preflight validate'}
              </SimButton>
              <SimButton onClick={cancelImport} disabled={importRunState !== 'running'} variant="secondary">
                Cancel
              </SimButton>
              <SimButton
                variant="secondary"
                onClick={() => {
                  setImportPath('');
                  setImportFile(null);
                  setImportError(null);
                  setImportLog([]);
                  setImportSummary(null);
                  setPreflightError(null);
                  setPreflightResult(null);
                  setImportSimulationCode('');
                  setImportSimulationVersion('');
                }}
                disabled={importRunState === 'running' || preflightRunning}
              >
                Clear
              </SimButton>
            </div>
            {importError && <div className="text-rose-300 text-sm">{importError}</div>}
            {preflightError && <div className="text-rose-300 text-sm">{preflightError}</div>}
            {importSummary && (
              <div className="rounded-input border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm text-emerald-100">
                <div className="font-semibold">Summary</div>
                <div>
                  Mode: {importSummary.mode}
                </div>
                <div>
                  Simulation: {importSummary.simulationName} v{importSummary.simulationVersion}
                </div>
                <div>
                  Variants: {importSummary.variantCount} • Screens: {importSummary.screenCount} • Rows: {importSummary.rowCount}
                </div>
                <div>Duration: {importSummary.elapsedMs}ms</div>
              </div>
            )}
            {importLog.length > 0 ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm font-semibold">Logs</div>
                  <SimButton variant="secondary" onClick={copyImportLogs} disabled={importLog.length === 0}>
                    Copy logs
                  </SimButton>
                </div>
                <pre className="max-h-64 min-h-20 overflow-auto rounded-input border border-line bg-surface-page/80 p-3 text-xs font-mono text-txt-secondary whitespace-pre-wrap">
                  {importLog.join('\n')}
                </pre>
              </div>
            ) : null}
            {preflightResult ? (
              <div className="space-y-2 rounded-input border border-line bg-surface-card/40 p-3">
                <div className="text-sm font-semibold">Preflight results</div>
                <div className="text-xs text-txt-secondary">
                  Status: {preflightResult.ok ? 'PASS' : 'FAIL'} • Errors: {preflightResult.errors.length} • Warnings:{' '}
                  {preflightResult.warnings.length}
                </div>
                {preflightResult.errors.length > 0 ? (
                  <div>
                    <div className="text-xs font-semibold text-rose-200">Errors</div>
                    <div className="max-h-56 overflow-auto space-y-1 text-xs">
                      {preflightResult.errors.map((issue, index) => (
                        <div
                          key={`${issue.rule}-${issue.sheet}-${issue.row}-${issue.key || index}`}
                          className="rounded border border-rose-500/30 bg-rose-500/10 px-2 py-1"
                        >
                          {issue.rule}: {issue.message} ({issue.sheet}:{issue.row})
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
                {preflightResult.warnings.length > 0 ? (
                  <div>
                    <div className="text-xs font-semibold text-amber-200">Warnings</div>
                    <div className="max-h-40 overflow-auto space-y-1 text-xs">
                      {preflightResult.warnings.map((issue, index) => (
                        <div
                          key={`${issue.rule}-${issue.sheet}-${issue.row}-${issue.key || index}`}
                          className="rounded border border-amber-500/30 bg-amber-500/10 px-2 py-1"
                        >
                          {issue.rule}: {issue.message} ({issue.sheet}:{issue.row})
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}
          </section>
        ) : null}

        {activeTab === 'simulations' ? (
          <section className="space-y-4">
            <SimLabel>Simulations</SimLabel>
            <SimBody>Default version: New links use this version when enabled. Old links keep the version they were created with.</SimBody>

            <div className="flex flex-wrap gap-2">
              <SimButton variant="secondary" onClick={refreshSimulations} disabled={simulationsLoading}>
                Refresh
              </SimButton>
            </div>
            {simulationsError ? <div className="text-sm text-rose-300">{simulationsError}</div> : null}
            {simulationsLoading ? <div>Loading simulations...</div> : null}

            <div className="space-y-2">
              {simulations.map((simulation) => {
                const activeVariantCount = simulation.active_version
                  ? variantCountBySimulationId.get(simulation.active_version.id) || 0
                  : 0;
                const hasNewerImportedVersion =
                  Boolean(simulation.latest_version) &&
                  (!simulation.active_version || simulation.latest_version?.id !== simulation.active_version.id);
                const isSelectedSimulation = selectedSimulationCode === simulation.simulation_code;
                const showVersionsPanel = isSelectedSimulation && selectedSimulationPanel === 'versions';
                const showAccessPanel =
                  isSelectedSimulation &&
                  selectedSimulationPanel === 'access' &&
                  simulation.visibility === 'PRIVATE';

                return (
                <div
                  key={simulation.simulation_code}
                  data-testid={`simulation-card-${simulation.simulation_code}`}
                  ref={(node) => {
                    simulationCardRefs.current[simulation.simulation_code] = node;
                  }}
                  className="rounded-input border border-line bg-surface-page/60 p-3 space-y-3"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="font-semibold">{simulation.name}</div>
                      <div className="text-sm text-txt-muted">
                        Code: {simulation.simulation_code} • Role: {simulation.role_family}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-txt-muted/80">
                        Versions: {simulation.version_count} • Variants: {activeVariantCount}
                      </span>
                      {!simulation.is_globally_enabled ? (
                        <span className="rounded-full bg-rose-500/20 px-2 py-1 text-xs text-rose-200">Disabled</span>
                      ) : null}
                    </div>
                  </div>

                  <div className="grid gap-3 md:grid-cols-2">
                    <div className="rounded border border-line bg-surface-page/50 p-3">
                      <div className="flex items-center justify-between gap-3">
                        <div className="text-sm font-semibold">Enabled</div>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={simulation.is_globally_enabled}
                          onClick={() =>
                            patchSimulationSettings(simulation.simulation_code, {
                              is_globally_enabled: !simulation.is_globally_enabled,
                            })
                          }
                          disabled={simulationUpdatingKey === simulation.simulation_code}
                          className={`relative inline-flex h-6 w-12 items-center rounded-full transition ${
                            simulation.is_globally_enabled ? 'bg-emerald-500' : 'bg-slate-700'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                              simulation.is_globally_enabled ? 'translate-x-7' : 'translate-x-1'
                            }`}
                          />
                        </button>
                      </div>
                      <div className="mt-1 text-xs text-txt-muted/90">
                        {simulation.is_globally_enabled
                          ? 'This simulation can be used (subject to visibility rules).'
                          : 'No company can use this simulation.'}
                      </div>
                    </div>

                    <div className="rounded border border-line bg-surface-page/50 p-3">
                      <div className="text-sm font-semibold">Visibility</div>
                      <div className="mt-2 inline-flex rounded-input border border-line p-1">
                        <button
                          type="button"
                          onClick={() =>
                            patchSimulationSettings(simulation.simulation_code, { visibility: 'PUBLIC' })
                          }
                          disabled={
                            simulationUpdatingKey === simulation.simulation_code ||
                            simulation.visibility === 'PUBLIC'
                          }
                          className={`rounded-md px-3 py-1 text-sm ${
                            simulation.visibility === 'PUBLIC'
                              ? 'bg-emerald-500 text-slate-950'
                              : 'text-txt-secondary hover:bg-slate-800'
                          }`}
                        >
                          Public
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            patchSimulationSettings(simulation.simulation_code, { visibility: 'PRIVATE' })
                          }
                          disabled={
                            simulationUpdatingKey === simulation.simulation_code ||
                            simulation.visibility === 'PRIVATE'
                          }
                          className={`rounded-md px-3 py-1 text-sm ${
                            simulation.visibility === 'PRIVATE'
                              ? 'bg-emerald-500 text-slate-950'
                              : 'text-txt-secondary hover:bg-slate-800'
                          }`}
                        >
                          Private
                        </button>
                      </div>
                      <div className="mt-1 text-xs text-txt-muted/90">
                        {simulation.visibility === 'PUBLIC'
                          ? 'All companies can use this simulation.'
                          : 'Only selected companies can use this simulation.'}
                      </div>
                    </div>
                  </div>

                  <div className="rounded border border-line bg-surface-page/50 p-3">
                    <div className="text-sm text-txt-primary">
                      Default version (used for new links when enabled):{' '}
                      {simulation.active_version
                        ? `${simulation.active_version.version} (${formatDateTime(simulation.active_version.created_at)})`
                        : 'None'}
                    </div>
                    {!simulation.active_version ? (
                      <div className="mt-1 text-xs text-amber-300">
                        Warning: No active version selected. New links cannot be created until you choose one.
                      </div>
                    ) : null}
                    {hasNewerImportedVersion && simulation.latest_version ? (
                      <div className="mt-1 text-xs text-brand-600">
                        Latest imported version: {simulation.latest_version.version} ({formatDateTime(simulation.latest_version.created_at)}) — open Versions to review and make active if needed.
                      </div>
                    ) : null}
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <SimButton
                      variant="secondary"
                      onClick={() => loadSimulationVersions(simulation.simulation_code)}
                      disabled={versionsLoading && selectedSimulationCode === simulation.simulation_code}
                    >
                      Versions
                    </SimButton>
                    {simulation.visibility === 'PRIVATE' ? (
                      <SimButton
                        variant="secondary"
                        onClick={() => loadSimulationAccess(simulation.simulation_code)}
                        disabled={accessLoading && selectedSimulationCode === simulation.simulation_code}
                      >
                        Access
                      </SimButton>
                    ) : null}
                    <SimButton
                      variant="secondary"
                      onClick={() => archiveSimulationFamily(simulation.simulation_code)}
                      disabled={simulationUpdatingKey === simulation.simulation_code}
                    >
                      Archive simulation
                    </SimButton>
                    {simulationUpdatingKey === simulation.simulation_code ? (
                      <div className="self-center text-xs text-txt-muted/90">Saving...</div>
                    ) : null}
                  </div>

                  {simulationCardErrors[simulation.simulation_code] ? (
                    <div className="rounded border border-rose-500/40 bg-rose-500/10 p-3 text-sm text-rose-200">
                      <div>{simulationCardErrors[simulation.simulation_code]}</div>
                      {simulationRetryPayloads[simulation.simulation_code] ? (
                        <div className="mt-2">
                          <SimButton
                            variant="secondary"
                            onClick={() =>
                              patchSimulationSettings(
                                simulation.simulation_code,
                                simulationRetryPayloads[simulation.simulation_code]
                              )
                            }
                          >
                            Retry
                          </SimButton>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                  <div className="text-xs text-txt-muted/90">
                    {simulation.visibility === 'PUBLIC'
                      ? 'Public: “All companies can use this simulation.”'
                      : 'Private: “Only selected companies can use this simulation.”'}
                  </div>
                  {showVersionsPanel ? (
                    <div
                      data-testid={`simulation-panel-versions-${simulation.simulation_code}`}
                      className="rounded-input border border-line bg-surface-page/50 p-3 space-y-3"
                    >
                      <div className="space-y-2">
                        <div className="text-sm font-semibold">Versions</div>
                        {versionsLoading ? <div className="text-sm text-txt-muted">Loading versions...</div> : null}
                        {versionsError ? (
                          <div className="rounded border border-rose-500/40 bg-rose-500/10 p-3 text-sm text-rose-200">
                            <div>{versionsError}</div>
                            <div className="mt-2">
                              <SimButton
                                variant="secondary"
                                onClick={() => loadSimulationVersions(simulation.simulation_code)}
                                disabled={versionsLoading}
                              >
                                Retry
                              </SimButton>
                            </div>
                          </div>
                        ) : null}
                        {!versionsLoading && simulationVersions.length === 0 ? (
                          <div className="text-sm text-txt-muted">No versions loaded.</div>
                        ) : null}
                        {simulationVersions.map((version) => (
                          <div
                            key={version.id}
                            className="rounded border border-line bg-surface-page/60 px-3 py-2 text-sm flex flex-wrap items-center justify-between gap-2"
                          >
                            <div>
                              {version.name} {version.version}{' '}
                              {version.isActiveVersion ? '(Default for new links)' : ''}
                              <div className="text-xs text-txt-muted/80">{formatDateTime(version.createdAt)}</div>
                              <div className="text-xs text-txt-muted/80">
                                {version.variantCount ?? 0} variants • {version.screenCount ?? 0} screens
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {!version.isActiveVersion ? (
                                <SimButton
                                  variant="secondary"
                                  onClick={() => makeSimulationVersionActive(simulation.simulation_code, version.id)}
                                  disabled={simulationUpdatingKey === `${simulation.simulation_code}:${version.id}`}
                                  title="Switch new links to this version (you can switch back anytime)."
                                >
                                  Make Active
                                </SimButton>
                              ) : null}
                              {!version.isActiveVersion ? (
                                <SimButton
                                  variant="secondary"
                                  onClick={() => archiveSimulationVersion(simulation.simulation_code, version.id)}
                                  disabled={simulationUpdatingKey === `${simulation.simulation_code}:archive:${version.id}`}
                                >
                                  Delete version
                                </SimButton>
                              ) : null}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}

                  {showAccessPanel ? (
                    <div
                      data-testid={`simulation-panel-access-${simulation.simulation_code}`}
                      className="rounded-input border border-line bg-surface-page/50 p-3 space-y-3"
                    >
                      <div className="space-y-2">
                        <div className="text-sm font-semibold">Company access</div>
                        {accessLoading ? <div className="text-sm text-txt-muted">Loading company access...</div> : null}
                        {accessError ? (
                          <div className="rounded border border-rose-500/40 bg-rose-500/10 p-3 text-sm text-rose-200">
                            <div>{accessError}</div>
                            <div className="mt-2">
                              <SimButton
                                variant="secondary"
                                onClick={() => loadSimulationAccess(simulation.simulation_code)}
                                disabled={accessLoading}
                              >
                                Retry
                              </SimButton>
                            </div>
                          </div>
                        ) : null}
                        {!accessLoading && simulationAccessRows.length === 0 ? (
                          <div className="text-sm text-txt-muted">No companies available.</div>
                        ) : null}
                        {simulationAccessRows.map((row) => (
                          <div
                            key={row.company_id}
                            className="rounded border border-line bg-surface-page/60 px-3 py-2 text-sm flex items-center justify-between gap-2"
                          >
                            <div>{row.company_name}</div>
                            <SimButton
                              variant="secondary"
                              onClick={() =>
                                updateSimulationCompanyAccess(simulation.simulation_code, row.company_id, !row.enabled)
                              }
                              disabled={simulationUpdatingKey === `${simulation.simulation_code}:${row.company_id}`}
                              title={
                                row.enabled
                                  ? 'Enabled for this company: This company can create links and run candidates for this simulation.'
                                  : 'Disabled for this company: This company cannot see or use this simulation.'
                              }
                            >
                              {row.enabled ? 'Enabled' : 'Disabled'}
                            </SimButton>
                          </div>
                        ))}
                        <div className="text-xs text-txt-muted/90">
                          Enabled for this company: “This company can create links and run candidates for this simulation.”
                        </div>
                        <div className="text-xs text-txt-muted/90">
                          Disabled for this company: “This company cannot see or use this simulation.”
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
                );
              })}
            </div>
          </section>
        ) : null}

        {activeTab === 'variants' ? (
          <section className="space-y-4">
            <SimLabel>Variants</SimLabel>
            <SimBody>Set the gold demo variant used by default for rotate policy attempts.</SimBody>
            {setGoldMessage && <div className="text-sm text-txt-secondary">{setGoldMessage}</div>}
            {variantsLoading ? (
              <div>Loading variants...</div>
            ) : (
              <div className="grid gap-3">
                {variants.map((variant) => (
                  <div
                    key={variant.id}
                    className="rounded-input border border-line bg-surface-page/60 p-3 flex flex-wrap items-center justify-between gap-3"
                  >
                    <div>
                      <div className="font-semibold">
                        {variant.simulation.name} {variant.simulation.version} — {variant.variant_code}
                      </div>
                      <div className="text-sm text-txt-muted">{variant.variant_name}</div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span
                        className={`rounded-full px-3 py-1 text-xs ${
                          variant.is_gold_demo
                            ? 'border border-emerald-500/30 bg-emerald-500/20 text-[#0f1a1a]'
                            : 'bg-brand-600 text-black'
                        }`}
                      >
                        {variant.is_gold_demo ? 'Gold' : 'Standard'}
                      </span>
                      {!variant.is_gold_demo ? (
                        <SimButton variant="secondary" onClick={() => setGoldDemo(variant.id)} className="h-9">
                          Set Gold
                        </SimButton>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        ) : null}

        {activeTab === 'credits' ? (
          <section className="space-y-4">
            <SimLabel>Companies + Credits</SimLabel>
            {walletError && <div className="text-rose-300 text-sm">{walletError}</div>}
            {walletLoading ? (
              <div>Loading companies...</div>
            ) : (
              <>
                <div className="grid gap-3 md:grid-cols-2">
                  <div>
                    <label className="text-sm text-txt-secondary">Company</label>
                    <select
                      aria-label="Company"
                      value={selectedCompanyId}
                      onChange={(event) => setSelectedCompanyId(event.target.value)}
                      className="mt-2 w-full rounded-input border border-line bg-surface-page/60 px-4 py-3 text-txt-primary"
                    >
                      {wallets.map((wallet) => (
                        <option key={getWalletId(wallet)} value={getWalletId(wallet)}>
                          {wallet.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm text-txt-secondary">Change amount</label>
                    <input
                      aria-label="Change amount"
                      value={topupAmount}
                      onChange={(event) => setTopupAmount(event.target.value)}
                      type="number"
                      className="mt-2 w-full rounded-input border border-line bg-surface-page/60 px-4 py-3 text-txt-primary"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm text-txt-secondary">Add reason</label>
                    <input
                      aria-label="Add reason"
                      value={topupReason}
                      onChange={(event) => setTopupReason(event.target.value)}
                      className="mt-2 w-full rounded-input border border-line bg-surface-page/60 px-4 py-3 text-txt-primary"
                      placeholder="Reason"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-txt-secondary">Reference</label>
                    <input
                      aria-label="Reference"
                      value={topupReference}
                      onChange={(event) => setTopupReference(event.target.value)}
                      className="mt-2 w-full rounded-input border border-line bg-surface-page/60 px-4 py-3 text-txt-primary"
                      placeholder="Reference"
                    />
                  </div>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <SimButton
                    onClick={submitTopup}
                    disabled={!canTopup || !selectedCompanyExists}
                    variant={canTopup && selectedCompanyExists ? 'primary' : 'secondary'}
                    className="w-full"
                  >
                    {topupSubmitting ? 'Adding...' : 'Add credits'}
                  </SimButton>
                  <SimButton
                    onClick={submitRemoveCredits}
                    disabled={!canRemoveCredits || !selectedCompanyExists}
                    variant={canRemoveCredits && selectedCompanyExists ? 'primary' : 'secondary'}
                    className="w-full"
                  >
                    {removeSubmitting ? 'Removing...' : 'Remove credits'}
                  </SimButton>
                </div>
                <div className="space-y-2">
                  {wallets.map((wallet) => (
                    <div
                      key={getWalletId(wallet)}
                      className="rounded-input border border-line bg-surface-page/70 p-3 text-sm"
                    >
                      <div className="font-semibold">{wallet.name}</div>
                      <div className="text-txt-muted">
                        Available: {wallet.wallet.credits_available} / Balance: {wallet.wallet.balance} (Reserved:{' '}
                        {wallet.wallet.reserved_balance})
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </section>
        ) : null}

        {activeTab === 'buildRequests' ? (
          <section className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <SimLabel>Build Requests</SimLabel>
                <SimBody>Update paid simulation builds as they move through build, final checks, and deployment.</SimBody>
              </div>
              <SimButton variant="secondary" onClick={refreshBuildRequests} disabled={buildRequestsLoading}>
                Refresh
              </SimButton>
            </div>
            {buildRequestsError ? <div className="text-sm text-rose-300">{buildRequestsError}</div> : null}
            {buildRequestsLoading ? <div>Loading build requests...</div> : null}
            {!buildRequestsLoading && buildRequests.length === 0 ? (
              <div className="rounded-input border border-line bg-surface-page/60 p-3 text-sm text-txt-muted">
                No build requests found.
              </div>
            ) : null}
            <div className="space-y-2">
              {buildRequests.map((request) => (
                <div key={request.id} className="rounded-input border border-line bg-surface-page/60 p-3 space-y-3">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="font-semibold">{request.roleName}</div>
                      <div className="text-sm text-txt-muted">
                        {request.company?.name || request.requesterCompanyName} • {request.offering.name}
                      </div>
                      <div className="text-xs text-txt-muted/80">
                        Payment: {request.paymentOrder?.paymentStatus || 'Not linked'} • Paid:{' '}
                        {formatDateTime(request.paymentOrder?.paidAt || null)}
                      </div>
                    </div>
                    <span className="rounded-full border border-[#0f766e] bg-[#ccfbf1] px-3 py-1 text-xs font-semibold text-[#115e59]">
                      {formatBuildRequestStatusLabel(request.status)}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <SimButton
                      variant="secondary"
                      onClick={() => updateBuildRequestStatus(request.id, 'PAID')}
                      disabled={buildRequestUpdatingId === request.id || request.status === 'PAID'}
                    >
                      Allocated for build
                    </SimButton>
                    <SimButton
                      variant="secondary"
                      onClick={() => updateBuildRequestStatus(request.id, 'IN_BUILD')}
                      disabled={buildRequestUpdatingId === request.id || request.status === 'IN_BUILD'}
                    >
                      Final checks
                    </SimButton>
                    <SimButton
                      variant="secondary"
                      onClick={() => updateBuildRequestStatus(request.id, 'DELIVERED')}
                      disabled={buildRequestUpdatingId === request.id || request.status === 'DELIVERED'}
                    >
                      Deployed
                    </SimButton>
                    {buildRequestUpdatingId === request.id ? (
                      <div className="self-center text-xs text-txt-muted/90">Saving...</div>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          </section>
        ) : null}

        {activeTab === 'payfastTest' && payFastLiveTestStatus?.enabled ? (
          <section className="space-y-4">
            <div>
              <SimLabel>PayFast Live Test</SimLabel>
              <SimBody>Start hidden R{payFastLiveTestStatus.amountZar || '10.00'} PayFast checks for the app and landing flows.</SimBody>
            </div>
            {payFastLiveTestError ? <div className="text-sm text-rose-300">{payFastLiveTestError}</div> : null}
            <div className="rounded-input border border-line bg-surface-page/60 p-4">
              <div className="grid gap-3 md:grid-cols-2">
                <div className="space-y-3">
                  <div>
                    <div className="font-semibold">App credit flow</div>
                    <div className="text-sm text-txt-muted">
                      Creates {payFastLiveTestStatus.appOfferingCode} and allocates one test credit after verified ITN.
                    </div>
                  </div>
                  <SimButton
                    onClick={() => void runPayFastLiveTest('app')}
                    disabled={payFastLiveTestSubmitting !== null}
                  >
                    {payFastLiveTestSubmitting === 'app' ? 'Starting...' : 'Run App PayFast Test'}
                  </SimButton>
                </div>
                <div className="space-y-3">
                  <div>
                    <div className="font-semibold">Landing build flow</div>
                    <div className="text-sm text-txt-muted">
                      Creates {payFastLiveTestStatus.landingOfferingCode} and a normal test build request.
                    </div>
                  </div>
                  <SimButton
                    onClick={() => void runPayFastLiveTest('landing')}
                    disabled={payFastLiveTestSubmitting !== null}
                  >
                    {payFastLiveTestSubmitting === 'landing' ? 'Starting...' : 'Run Landing PayFast Test'}
                  </SimButton>
                </div>
              </div>
            </div>
          </section>
        ) : null}

        {activeTab === 'links' ? (
          <section className="space-y-4">
            <div className="flex flex-wrap gap-3">
              <div>
                <label className="text-xs text-txt-muted">Search</label>
                <input
                  value={currentFilters.links.q}
                  onChange={(event) => setFilter('links', 'q', event.target.value)}
                  className="rounded-input border border-line bg-surface-page/60 px-3 py-2 text-sm text-txt-primary"
                  placeholder="Search company, role, variant..."
                />
              </div>
              <div>
                <label className="text-xs text-txt-muted">Company</label>
                <select
                  value={currentFilters.links.company_id}
                  onChange={(event) => setFilter('links', 'company_id', event.target.value)}
                  className="rounded-input border border-line bg-surface-page/60 px-3 py-2 text-sm text-txt-primary"
                >
                  <option value="">All companies</option>
                  {wallets.map((wallet) => (
                    <option key={getWalletId(wallet)} value={getWalletId(wallet)}>
                      {wallet.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-txt-muted">Status</label>
                <select
                  value={currentFilters.links.status}
                  onChange={(event) => setFilter('links', 'status', event.target.value)}
                  className="rounded-input border border-line bg-surface-page/60 px-3 py-2 text-sm text-txt-primary"
                >
                  <option>ALL</option>
                  <option>ACTIVE</option>
                  <option>EXPIRED</option>
                  <option>DISABLED</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-txt-muted">Date from</label>
                <input
                  value={currentFilters.links.date_from}
                  onChange={(event) => setFilter('links', 'date_from', event.target.value)}
                  type="date"
                  className="rounded-input border border-line bg-surface-page/60 px-3 py-2 text-sm text-txt-primary"
                />
              </div>
              <div>
                <label className="text-xs text-txt-muted">Date to</label>
                <input
                  value={currentFilters.links.date_to}
                  onChange={(event) => setFilter('links', 'date_to', event.target.value)}
                  type="date"
                  className="rounded-input border border-line bg-surface-page/60 px-3 py-2 text-sm text-txt-primary"
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <SimButton variant="secondary" onClick={reloadLinks} disabled={links.loading}>
                Refresh
              </SimButton>
              <SimButton variant="secondary" onClick={() => clearFilters('links')} disabled={links.loading}>
                Clear filters
              </SimButton>
            </div>
            {links.error ? (
              <div className="space-y-2">
                <div className="text-rose-300 text-sm">{links.error}</div>
                <SimButton variant="secondary" onClick={reloadLinks} disabled={links.loading}>
                  Retry
                </SimButton>
              </div>
            ) : null}
            <div className="space-y-2">
              {links.items.length === 0 && !links.loading ? (
                <div className="text-sm text-txt-muted">No links found.</div>
              ) : null}
              {links.items.map((link) => (
                <div
                  key={link.id}
                  className="rounded-input border border-line bg-surface-page/60 p-3 text-sm flex flex-wrap items-start justify-between gap-2"
                >
                  <div>
                    <div className="font-semibold">{link.company.name}</div>
                    <div className="text-txt-muted">
                      {link.simulation.name} {link.simulation.version}
                    </div>
                    <div className="text-txt-muted">
                      {link.variant_code ? `${link.variant_code} / ${link.variant_name || '—'}` : link.variant_policy}
                    </div>
                    {link.job_title ? <div>Job: {link.job_title}</div> : null}
                    <div className="mt-1 break-all font-mono text-xs text-txt-muted">{link.public_url}</div>
                    <div className="text-xs text-txt-muted/80">Internal ID: {link.id}</div>
                    <div className="text-xs text-txt-muted/80">Created: {formatDateTime(link.created_at)}</div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <span
                      className={`rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-[0.08em] ${getLinkStatusPillClass(link.status)}`}
                    >
                      {formatLinkStatusLabel(link.status)}
                    </span>
                    <div className="flex flex-wrap justify-end gap-2">
                      <SimButton
                        variant="secondary"
                        onClick={async () => {
                          try {
                            await readClipboard(link.public_url);
                            toast.success('Candidate link copied.');
                          } catch (_error) {
                            toast.error('Unable to copy candidate link.');
                          }
                        }}
                      >
                        Copy Candidate Link
                      </SimButton>
                      <SimButton
                        variant="secondary"
                        onClick={async () => {
                          try {
                            await readClipboard(link.id);
                            toast.success('Internal link ID copied.');
                          } catch (_error) {
                            toast.error('Unable to copy internal link ID.');
                          }
                        }}
                      >
                        Copy Internal ID
                      </SimButton>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {links.loading ? <div>Loading...</div> : null}
            {links.hasMore ? (
              <SimButton
                onClick={() => links.loadMore('/sim/admin/links', linkListParams)}
                disabled={links.loading || Boolean(links.error)}
              >
                {links.loading ? 'Loading...' : 'Load more'}
              </SimButton>
            ) : null}
          </section>
        ) : null}

        {activeTab === 'attempts' ? (
          <section className="space-y-4">
            <div className="flex flex-wrap gap-3">
              <div>
                <label className="text-xs text-txt-muted">Search</label>
                <input
                  value={currentFilters.attempts.q}
                  onChange={(event) => setFilter('attempts', 'q', event.target.value)}
                  className="rounded-input border border-line bg-surface-page/60 px-3 py-2 text-sm text-txt-primary"
                  placeholder="Search candidate, attempt id, variant..."
                />
              </div>
              <div>
                <label className="text-xs text-txt-muted">Company</label>
                <select
                  value={currentFilters.attempts.company_id}
                  onChange={(event) => setFilter('attempts', 'company_id', event.target.value)}
                  className="rounded-input border border-line bg-surface-page/60 px-3 py-2 text-sm text-txt-primary"
                >
                  <option value="">All companies</option>
                  {wallets.map((wallet) => (
                    <option key={getWalletId(wallet)} value={getWalletId(wallet)}>
                      {wallet.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-txt-muted">Variant</label>
                <select
                  value={currentFilters.attempts.variant_id}
                  onChange={(event) => setFilter('attempts', 'variant_id', event.target.value)}
                  className="rounded-input border border-line bg-surface-page/60 px-3 py-2 text-sm text-txt-primary"
                >
                  <option value="">All variants</option>
                  {variants.map((variant) => (
                    <option key={variant.id} value={variant.id}>
                      {variant.variant_code} ({variant.simulation.name})
                    </option>
                  ))}
                </select>
              </div>
              <select
                value={currentFilters.attempts.status}
                onChange={(event) => setFilter('attempts', 'status', event.target.value)}
                className="rounded-input border border-line bg-surface-page/60 px-3 py-2 text-sm text-txt-primary"
              >
                <option>ALL</option>
                <option>IN_PROGRESS</option>
                <option>COMPLETED</option>
                <option>EXPIRED</option>
              </select>
              <div>
                <label className="text-xs text-txt-muted">Date from</label>
                <input
                  value={currentFilters.attempts.date_from}
                  onChange={(event) => setFilter('attempts', 'date_from', event.target.value)}
                  type="date"
                  className="rounded-input border border-line bg-surface-page/60 px-3 py-2 text-sm text-txt-primary"
                />
              </div>
              <div>
                <label className="text-xs text-txt-muted">Date to</label>
                <input
                  value={currentFilters.attempts.date_to}
                  onChange={(event) => setFilter('attempts', 'date_to', event.target.value)}
                  type="date"
                  className="rounded-input border border-line bg-surface-page/60 px-3 py-2 text-sm text-txt-primary"
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <SimButton variant="secondary" onClick={reloadAttempts} disabled={attempts.loading}>
                Refresh
              </SimButton>
              <SimButton variant="secondary" onClick={() => clearFilters('attempts')} disabled={attempts.loading}>
                Clear filters
              </SimButton>
            </div>
            {attempts.error ? (
              <div className="space-y-2">
                <div className="text-rose-300 text-sm">{attempts.error}</div>
                <SimButton variant="secondary" onClick={reloadAttempts} disabled={attempts.loading}>
                  Retry
                </SimButton>
              </div>
            ) : null}
            <div className="space-y-2">
              {attempts.items.length === 0 && !attempts.loading ? (
                <div className="text-sm text-txt-muted">No attempts found.</div>
              ) : null}
              {attempts.items.map((attempt) => (
                <div
                  key={attempt.id}
                  className="rounded-input border border-line bg-surface-page/60 p-3 text-sm flex flex-wrap items-start justify-between gap-2"
                >
                  <div>
                    <div className="font-semibold">Attempt {attempt.id}</div>
                    <div>{attempt.company.name}</div>
                    <div>{attempt.variant.code}</div>
                    <div className="text-txt-muted">{formatCandidateLabel(attempt.candidate)}</div>
                    <div className="text-xs text-txt-muted/80">
                      Completed: {attempt.completed_at ? formatDateTime(attempt.completed_at) : 'in progress'}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-txt-muted">Status: {attempt.status}</div>
                    <div className="text-xs text-txt-muted">Integrity: {attempt.integrity_rating || '—'}</div>
                  </div>
                </div>
              ))}
            </div>
            {attempts.loading ? <div>Loading...</div> : null}
            {attempts.hasMore ? (
              <SimButton
                onClick={() => attempts.loadMore('/sim/admin/attempts', attemptListParams)}
                disabled={attempts.loading || Boolean(attempts.error)}
              >
                {attempts.loading ? 'Loading...' : 'Load more'}
              </SimButton>
            ) : null}
          </section>
        ) : null}

        {activeTab === 'reports' ? (
          <section className="space-y-4">
            <div className="flex flex-wrap gap-3">
              <div>
                <label className="text-xs text-txt-muted">Search</label>
                <input
                  value={currentFilters.reports.q}
                  onChange={(event) => setFilter('reports', 'q', event.target.value)}
                  className="rounded-input border border-line bg-surface-page/60 px-3 py-2 text-sm text-txt-primary"
                  placeholder="Search company, variant, candidate..."
                />
              </div>
              <div>
                <label className="text-xs text-txt-muted">Company</label>
                <select
                  value={currentFilters.reports.company_id}
                  onChange={(event) => setFilter('reports', 'company_id', event.target.value)}
                  className="rounded-input border border-line bg-surface-page/60 px-3 py-2 text-sm text-txt-primary"
                >
                  <option value="">All companies</option>
                  {wallets.map((wallet) => (
                    <option key={getWalletId(wallet)} value={getWalletId(wallet)}>
                      {wallet.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-txt-muted">Variant</label>
                <select
                  value={currentFilters.reports.variant_id}
                  onChange={(event) => setFilter('reports', 'variant_id', event.target.value)}
                  className="rounded-input border border-line bg-surface-page/60 px-3 py-2 text-sm text-txt-primary"
                >
                  <option value="">All variants</option>
                  {variants.map((variant) => (
                    <option key={variant.id} value={variant.id}>
                      {variant.variant_code} ({variant.simulation.name})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-txt-muted">Date from</label>
                <input
                  value={currentFilters.reports.date_from}
                  onChange={(event) => setFilter('reports', 'date_from', event.target.value)}
                  type="date"
                  className="rounded-input border border-line bg-surface-page/60 px-3 py-2 text-sm text-txt-primary"
                />
              </div>
              <div>
                <label className="text-xs text-txt-muted">Date to</label>
                <input
                  value={currentFilters.reports.date_to}
                  onChange={(event) => setFilter('reports', 'date_to', event.target.value)}
                  type="date"
                  className="rounded-input border border-line bg-surface-page/60 px-3 py-2 text-sm text-txt-primary"
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <SimButton variant="secondary" onClick={reloadReports} disabled={reports.loading}>
                Refresh
              </SimButton>
              <SimButton variant="secondary" onClick={() => clearFilters('reports')} disabled={reports.loading}>
                Clear filters
              </SimButton>
            </div>
            {reports.error ? (
              <div className="space-y-2">
                <div className="text-rose-300 text-sm">{reports.error}</div>
                <SimButton variant="secondary" onClick={reloadReports} disabled={reports.loading}>
                  Retry
                </SimButton>
              </div>
            ) : null}
            <div className="space-y-2">
              {reports.items.length === 0 && !reports.loading ? (
                <div className="text-sm text-txt-muted">No reports found.</div>
              ) : null}
              {reports.items.map((report) => (
                <div
                  key={report.id}
                  className="rounded-input border border-line bg-surface-page/60 p-3 text-sm flex flex-wrap gap-3 items-start justify-between"
                >
                  <div>
                    <div className="font-semibold">Report {report.id}</div>
                    <div>Company: {report.company.name}</div>
                    <div>Status: {report.status}</div>
                    {report.variant ? <div>Variant: {report.variant.code}</div> : null}
                    <div>Created: {formatDateTime(report.created_at)}</div>
                    <div>
                      {report.performance.percent}% • {report.performance.band}
                    </div>
                    {report.candidate ? (
                      <div>
                        {report.candidate.full_name} ({report.candidate.sa_id_last4})
                      </div>
                    ) : null}
                  </div>
                  {report.report_url ? (
                    <a
                      href={report.report_url}
                      className="inline-flex items-center rounded-input border border-line px-3 py-2 text-xs text-txt-primary hover:bg-slate-800"
                    >
                      Open report
                    </a>
                  ) : null}
                </div>
              ))}
            </div>
            {reports.loading ? <div>Loading...</div> : null}
            {reports.hasMore ? (
              <SimButton
                onClick={() => reports.loadMore('/sim/admin/reports', reportListParams)}
                disabled={reports.loading || Boolean(reports.error)}
              >
                {reports.loading ? 'Loading...' : 'Load more'}
              </SimButton>
            ) : null}
          </section>
        ) : null}
      </SimCard>
      </SimShell>
    </div>
  );
};

export default EmployerAdminDashboard;
